import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, preventShiftSelection, type QualityZoneColor, zoneBarClass } from '@/shared/lib';
import type { IndicatorSelectHandler } from '../../model/useIndicatorTransitions';
import { fmt } from '../utils';
import { EmptyState } from './EmptyState';

/** Один элемент горизонтальной столбчатой диаграммы */
export type BarChartItem = {
  label: string;
  /** Показанная величина; `null` — величины нет, вместо неё стоит прочерк */
  value: number | null;
  /** Необязательный суффикс у значения (например, «%») */
  suffix?: string;
  /** Разговоры показателя — по ним строка открывает список диалогов; пусто — строка не кликабельна */
  conversationIds?: number[];
  /**
   * Максимум шкалы показателя. Если задан — ширина бара нормируется его долей `value/maxScore`,
   * а рядом со значением показывается сам максимум («avg / max»). Без него ширина нормируется по
   * общему `maxValue` диаграммы, а если и его нет — бар остаётся минимальной ширины.
   */
  maxScore?: number | null;
  /**
   * Готовая доля заполнения бара (0..1) — приоритетнее `maxScore` и общего `maxValue`.
   *
   * Нужна показателю, собранному из оценок с разными шкалами: их баллы несопоставимы, и долю
   * заполнения считает бэкенд по каждой оценке отдельно, а не клиент делением среднего балла
   * на чужой максимум.
   */
  share?: number | null;
  /** Зона окраски бара по единой шкале зон; при отсутствии бар красится дефолтно (`negative`/`primary`). */
  zone?: QualityZoneColor;
};

/** Форматирует максимум шкалы: целое — без дробной части, иначе с одним знаком. */
const formatMax = (max: number): string => (Number.isInteger(max) ? String(max) : max.toFixed(1));

type Props = {
  items: BarChartItem[];
  /**
   * Максимум шкалы, к которому нормируется ширина баров. Не нужен диаграмме, где каждый бар
   * нормируется своей долей (`share`) или своим максимумом (`maxScore`), — там его не задают.
   */
  maxValue?: number;
  /** true — красные бары (для «плохих» метрик: процент низких оценок и т.п.) */
  negative?: boolean;
  /** Переход к диалогам строки; не задан — строки остаются некликабельными */
  onSelect?: IndicatorSelectHandler;
};

/**
 * Горизонтальная столбчатая диаграмма: подпись — бар — значение.
 * Подпись усечена до 180px, бар занимает оставшуюся ширину.
 *
 * Кликабельность выводится из данных: есть разговоры и обработчик — строка ведёт к диалогам,
 * нет — обычная строка графика. Элемент опознаётся позицией, а не подписью: подписи в пределах
 * одной карточки могут совпасть (однофамильцы в рейтинге), и клик увёл бы к чужой выборке.
 */
export const BarChart: FC<Props> = ({ items, maxValue, negative, onSelect }) => {
  const { t } = useTranslation('qualityDashboard');

  if (items.length === 0) return <EmptyState />;

  const rowClassName = 'grid grid-cols-[minmax(100px,180px)_1fr_72px] gap-2 items-center';

  return (
    <div className="flex flex-col gap-2">
      {items.map((item, index) => {
        // Готовая доля заполнения — первична; иначе нормируем по своему максимуму шкалы,
        // а при его отсутствии — по общему максимуму диаграммы.
        const hasOwnMax = typeof item.maxScore === 'number' && item.maxScore > 0;
        const denom = hasOwnMax ? (item.maxScore as number) : (maxValue ?? 0);
        const ownPct = denom > 0 && item.value !== null ? (item.value / denom) * 100 : 0;
        const pct = typeof item.share === 'number' ? item.share * 100 : ownPct;
        const width = `${Math.max(2, Math.min(100, pct))}%`;
        const valueSuffix = hasOwnMax ? ` / ${formatMax(item.maxScore as number)}` : (item.suffix ?? '');
        const valueText = item.value === null ? fmt(item.value) : `${fmt(item.value)}${valueSuffix}`;
        const fillClass = item.zone ? zoneBarClass[item.zone] : negative ? 'bg-destructive/60' : 'bg-primary';
        const isClickable = !!onSelect && !!item.conversationIds?.length;
        const rowContent = (
          <>
            <span className="text-sm truncate">{item.label}</span>
            <div className="h-4 bg-muted rounded-full overflow-hidden">
              <div className={cn('h-full rounded-full transition-all', fillClass)} style={{ width }} />
            </div>
            <span className="text-sm font-bold text-right">{valueText}</span>
          </>
        );

        if (!isClickable) {
          return (
            <div key={index} className={rowClassName}>
              {rowContent}
            </div>
          );
        }

        return (
          <div
            key={index}
            role="button"
            tabIndex={-1}
            aria-label={t('charts.openDialogs')}
            className={cn(rowClassName, 'cursor-pointer rounded-md hover:bg-muted/50')}
            onMouseDown={preventShiftSelection}
            onClick={(event) => onSelect?.(item, event.shiftKey)}
          >
            {rowContent}
          </div>
        );
      })}
    </div>
  );
};
